import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plus, Loader2, MoreVertical, Percent } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * Manual Order Modal — phone-sale flow for bragus.ro.
 *
 * Once submitted the new doc lands in `db.orders` with
 * `_integration_key="manual_bragus"`, status=3. The user can then click
 * the standard „Generează factură" + „Generează AWB" buttons in the
 * Orders list — both flows already work for non-eMAG / non-Gomag orders
 * because the doc carries all the needed fields (customer, products,
 * shipping_tax with delivery_mode="home").
 */
export default function ManualOrderModal({ open, onClose, onCreated }) {
  const [shipDefaults, setShipDefaults] = useState({ home: 17.99, locker: 12.99, vat_pct: 21 });
  const [form, setForm] = useState({
    customer_name: "", phone: "", email: "",
    address: "", county: "", city: "", postal_code: "",
    shipping_price: "17.99", shipping_vat: 21,
    delivery_mode: "home",    // "home" | "locker"
    payment_mode: "ramburs",  // "ramburs" | "online" (paid card, marked incasat)
    locker_id: "", locker_name: "", locker_address: "",
    // ── PJ (Persoana Juridica) — Feb 2026. When is_company=true, we
    // send `company_name`, `cui`, `contact_name` instead of `customer_name`
    // and the invoice goes to the company at Oblio.
    is_company: false,
    company_name: "", cui: "", contact_name: "",
  });
  const [products, setProducts] = useState([
    { sku: "", name: "", price: "", qty: 1, vat: 21, discount: null },
  ]);
  const [actionMenuFor, setActionMenuFor] = useState(null);   // row index for the 3-dot menu
  const [discountModalFor, setDiscountModalFor] = useState(null);
  const [discountDraft, setDiscountDraft] = useState({ type: "percent", value: "" });
  const [counties, setCounties] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [catalog, setCatalog] = useState([]); // [{sku, name, price}] for autocomplete
  const [lockers, setLockers] = useState([]);
  const [loadingLockers, setLoadingLockers] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/sameday/counties`).then((r) => setCounties(r.data?.items || r.data || [])).catch(() => {});
    // Lightweight catalog for SKU/name autocomplete (same source as Imports)
    axios.get(`${API}/products/catalog`)
      .then((r) => setCatalog((r.data?.items || []).map((p) => ({ sku: p.sku, name: p.name || "", price: p.price }))))
      .catch(() => {});
    // Fetch current shipping defaults from Settings so a new manual order
    // uses the LATEST rates (fixes bug where old hardcoded 17.99/12.99 was
    // used even after user updated Settings → Taxe livrare).
    axios.get(`${API}/settings/manual-order-shipping`)
      .then((r) => {
        const home = Number(r.data?.home ?? 17.99);
        const locker = Number(r.data?.locker ?? 12.99);
        const vat_pct = Number(r.data?.vat_pct ?? 21);
        setShipDefaults({ home, locker, vat_pct });
        setForm((f) => ({
          ...f,
          shipping_price: String((f.delivery_mode === "locker" ? locker : home).toFixed(2)),
          shipping_vat: vat_pct,
        }));
      })
      .catch(() => { /* keep hardcoded fallback */ });
    // reset on open
    setForm({ customer_name: "", phone: "", email: "", address: "",
              county: "", city: "", postal_code: "",
              shipping_price: "17.99", shipping_vat: 21,
              delivery_mode: "home",
              payment_mode: "ramburs",
              locker_id: "", locker_name: "", locker_address: "",
              is_company: false, company_name: "", cui: "", contact_name: "" });
    setProducts([{ sku: "", name: "", price: "", qty: 1, vat: 21, discount: null }]);
    setCities([]);
    setLockers([]);
  }, [open]);

  // Load cities when county changes. Sameday cities API requires `county_id`
  // (int) — the dropdown stores the county NAME, so we look up the id from
  // the counties[] list first. Without this the API 422s and the city
  // dropdown stays empty, which blocked "Creează comanda".
  useEffect(() => {
    if (!form.county) { setCities([]); return; }
    const countyObj = counties.find((c) => (c.name || c) === form.county);
    const countyId = countyObj?.id;
    if (!countyId) { setCities([]); return; }
    setLoadingCities(true);
    axios.get(`${API}/sameday/cities`, { params: { county_id: countyId } })
      .then((r) => setCities(r.data?.items || r.data || []))
      .catch(() => setCities([]))
      .finally(() => setLoadingCities(false));
  }, [form.county, counties]);

  // Fetch Sameday lockers for the selected city when delivery_mode="locker".
  // Reset locker selection whenever the city (or mode) changes.
  useEffect(() => {
    setLockers([]);
    setForm((f) => ({ ...f, locker_id: "", locker_name: "", locker_address: "" }));
    if (form.delivery_mode !== "locker" || !form.city) return;
    setLoadingLockers(true);
    axios.get(`${API}/sameday/lockers`, { params: { country: "ro", city: form.city } })
      .then((r) => setLockers(r.data?.lockers || []))
      .catch(() => setLockers([]))
      .finally(() => setLoadingLockers(false));
  }, [form.city, form.delivery_mode]);

  // Snap shipping price to the mode-specific default whenever the mode flips.
  // Reads the LIVE rates from Settings (loaded on open). User can still
  // override manually per order.
  useEffect(() => {
    const target = form.delivery_mode === "locker" ? shipDefaults.locker : shipDefaults.home;
    setForm((f) => ({ ...f, shipping_price: String(Number(target).toFixed(2)) }));
  }, [form.delivery_mode, shipDefaults.home, shipDefaults.locker]);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setProductField = (idx, k) => (e) => {
    const next = [...products];
    next[idx] = { ...next[idx], [k]: e.target.value };
    setProducts(next);
  };
  // Merge multiple fields into a product row (used by autocomplete pick)
  const mergeProduct = (idx, patch) => {
    const next = [...products];
    next[idx] = { ...next[idx], ...patch };
    setProducts(next);
  };
  const addProduct = () => setProducts([...products, { sku: "", name: "", price: "", qty: 1, vat: 21, discount: null }]);
  const removeProduct = (idx) => setProducts(products.filter((_, i) => i !== idx));

  // Returns the GROSS line total after applying any per-product discount.
  // discount.type = "percent" → subtract (price × qty) × (value/100)
  // discount.type = "fixed"   → subtract `value` lei from the gross line total
  const lineTotal = (p) => {
    const gross = (Number(p.price) || 0) * (Number(p.qty) || 0);
    if (!p.discount || !p.discount.value) return gross;
    const v = Number(p.discount.value) || 0;
    if (p.discount.type === "percent") return Math.max(0, gross * (1 - v / 100));
    return Math.max(0, gross - v);
  };
  const lineDiscountAmount = (p) => {
    const gross = (Number(p.price) || 0) * (Number(p.qty) || 0);
    return gross - lineTotal(p);
  };

  const openDiscount = (idx) => {
    const cur = products[idx]?.discount;
    setDiscountDraft(cur ? { type: cur.type, value: String(cur.value) } : { type: "percent", value: "" });
    setDiscountModalFor(idx);
    setActionMenuFor(null);
  };
  const applyDiscount = () => {
    const v = Number(discountDraft.value) || 0;
    const next = [...products];
    next[discountModalFor] = {
      ...next[discountModalFor],
      discount: v > 0 ? { type: discountDraft.type, value: v } : null,
    };
    setProducts(next);
    setDiscountModalFor(null);
  };
  const clearDiscount = (idx) => {
    const next = [...products];
    next[idx] = { ...next[idx], discount: null };
    setProducts(next);
    setActionMenuFor(null);
  };

  const productsTotal = products.reduce((s, p) => s + lineTotal(p), 0);
  const grandTotal = productsTotal + (Number(form.shipping_price) || 0);

  const submit = async () => {
    // Validare separată PF vs PJ
    if (form.is_company) {
      if (!form.company_name || !form.cui || !form.contact_name) {
        toast.error("PJ: completează Nume firmă, CUI și Persoana de contact.");
        return;
      }
      const cuiClean = form.cui.trim().toUpperCase().replace(/\s+/g, "");
      if (!/^(RO|MD)?\d{2,}$/.test(cuiClean)) {
        toast.error("CUI invalid. Format acceptat: RO12345678 sau 12345678.");
        return;
      }
    } else {
      if (!form.customer_name) {
        toast.error("Completează numele clientului.");
        return;
      }
    }
    if (!form.phone || !form.address || !form.county || !form.city) {
      toast.error("Completează telefon, adresă, județ și oraș.");
      return;
    }
    if (form.delivery_mode === "locker" && !form.locker_id) {
      toast.error("Selectează un locker EasyBox.");
      return;
    }
    const cleanProducts = products
      .filter((p) => (p.name || p.sku) && Number(p.price) > 0)
      .map((p) => ({
        sku: p.sku, name: p.name,
        price: Number(p.price),
        qty: Number(p.qty) || 1,
        vat: Number(p.vat) || 21,
        discount: p.discount && Number(p.discount.value) > 0 ? {
          type: p.discount.type,
          value: Number(p.discount.value),
        } : null,
      }));
    if (cleanProducts.length === 0) {
      toast.error("Adaugă cel puțin un produs cu preț > 0.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/orders/manual`, {
        ...form,
        // Când e PJ, `customer_name` trece pe backend gol; backend folosește
        // `company_name` (pt Oblio) și `contact_name` (pt AWB/livrare).
        customer_name: form.is_company ? form.contact_name : form.customer_name,
        shipping_price: Number(form.shipping_price) || 0,
        shipping_vat: Number(form.shipping_vat) || 21,
        products: cleanProducts,
      });
      toast.success(`Comandă creată: ${r.data?.order?.id}`);
      onCreated?.(r.data?.order);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div data-testid="manual-order-modal"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl max-h-[94vh] overflow-hidden flex flex-col">

        <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
          <h2 className="text-base font-semibold text-zinc-100">Comandă manuală — Bragus.ro</h2>
          <button onClick={onClose} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"><X className="h-5 w-5" /></button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-5 flex-1">
          {/* Customer */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wider text-orange-300">Date client</p>
              <div className="inline-flex rounded-md border border-zinc-800 bg-zinc-900 p-0.5 text-[11px]">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, is_company: false })}
                  data-testid="mo-toggle-pf"
                  className={`px-3 py-1 rounded ${!form.is_company ? "bg-orange-500 text-white font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}
                >
                  Persoană Fizică
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, is_company: true })}
                  data-testid="mo-toggle-pj"
                  className={`px-3 py-1 rounded ${form.is_company ? "bg-orange-500 text-white font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}
                >
                  Persoană Juridică
                </button>
              </div>
            </div>
            {form.is_company && (
              <div className="mb-3 rounded-md border border-orange-500/30 bg-orange-500/5 p-3 space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    data-testid="mo-company-name"
                    value={form.company_name}
                    onChange={setField("company_name")}
                    placeholder="Nume firmă (ex: NIVERO CONCEPT SRL) *"
                    className="rounded-md border border-orange-500/40 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500"
                  />
                  <input
                    data-testid="mo-cui"
                    value={form.cui}
                    onChange={setField("cui")}
                    placeholder="CUI (ex: RO46972872) *"
                    className="rounded-md border border-orange-500/40 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500 uppercase"
                  />
                </div>
                <input
                  data-testid="mo-contact-name"
                  value={form.contact_name}
                  onChange={setField("contact_name")}
                  placeholder="Persoană de contact (primește coletul) *"
                  className="w-full rounded-md border border-orange-500/40 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500"
                />
                <p className="text-[10px] text-orange-300/80 leading-snug">
                  Factura Oblio va fi emisă către <b>{form.company_name || "—"}</b>, CUI <b>{form.cui || "—"}</b>.
                  {form.cui && form.cui.toUpperCase().startsWith("RO") ? " (plătitor de TVA)" : (form.cui ? " (neplătitor de TVA)" : "")}
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {!form.is_company && (
                <input data-testid="mo-name" value={form.customer_name} onChange={setField("customer_name")} placeholder="Nume prenume *" className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
              )}
              <input data-testid="mo-phone" value={form.phone} onChange={setField("phone")} placeholder="Telefon *" className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
              <input data-testid="mo-email" value={form.email} onChange={setField("email")} placeholder="Email (opțional, pentru factură)" className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
              <input data-testid="mo-postal" value={form.postal_code} onChange={setField("postal_code")} placeholder="Cod poștal (opțional)" className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
            </div>
            <input data-testid="mo-address" value={form.address} onChange={setField("address")} placeholder="Adresa completă (stradă, nr., bloc, ap.) *" className="mt-3 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
            <div className="mt-3 grid grid-cols-2 gap-3">
              <select data-testid="mo-county" value={form.county} onChange={setField("county")} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50">
                <option value="">Județ *</option>
                {counties.map((c) => <option key={c.name || c} value={c.name || c}>{c.name || c}</option>)}
              </select>
              <select data-testid="mo-city" value={form.city} onChange={setField("city")} disabled={!form.county || loadingCities} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50 disabled:opacity-50">
                <option value="">{loadingCities ? "Se încarcă..." : "Oraș *"}</option>
                {cities.map((c) => <option key={c.name || c} value={c.name || c}>{c.name || c}</option>)}
              </select>
            </div>
          </section>

          {/* Products */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wider text-orange-300">Produse</p>
              <button data-testid="mo-add-product" onClick={addProduct} className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-200 hover:bg-emerald-500/20">
                <Plus className="h-3 w-3" /> Adaugă produs
              </button>
            </div>
            <div className="space-y-2">
              {products.map((p, i) => (
                <div key={i} data-testid={`mo-product-row-${i}`}>
                  <div className="grid grid-cols-[1fr_2fr_90px_60px_70px_28px] gap-2 items-center">
                    <MoAutocomplete
                      testid={`mo-sku-${i}`}
                      value={p.sku}
                      placeholder="SKU"
                      mono
                      onChange={(v) => {
                        mergeProduct(i, { sku: v });
                        const exact = catalog.find((c) => c.sku.toLowerCase() === v.trim().toLowerCase());
                        if (exact) {
                          const patch = { sku: exact.sku };
                          if (!p.name) patch.name = exact.name;
                          if (!p.price && exact.price) patch.price = String(exact.price);
                          mergeProduct(i, patch);
                        }
                      }}
                      onPick={(c) => mergeProduct(i, {
                        sku: c.sku, name: c.name,
                        ...(p.price ? {} : (c.price ? { price: String(c.price) } : {})),
                      })}
                      suggestions={
                        (() => {
                          const q = (p.sku || "").trim().toLowerCase();
                          return q
                            ? catalog.filter((c) => c.sku.toLowerCase().includes(q)).slice(0, 20)
                            : catalog.slice(0, 20);
                        })()
                      }
                    />
                    <MoAutocomplete
                      testid={`mo-name-${i}`}
                      value={p.name}
                      placeholder="Nume produs *"
                      onChange={(v) => mergeProduct(i, { name: v })}
                      onPick={(c) => mergeProduct(i, {
                        sku: c.sku, name: c.name,
                        ...(p.price ? {} : (c.price ? { price: String(c.price) } : {})),
                      })}
                      suggestions={
                        (() => {
                          const q = (p.name || "").trim().toLowerCase();
                          return q.length >= 2
                            ? catalog.filter((c) => (c.name || "").toLowerCase().includes(q)).slice(0, 20)
                            : [];
                        })()
                      }
                    />
                    <input value={p.price} onChange={setProductField(i, "price")} type="number" step="0.01" placeholder="Preț (cu TVA)" className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-right text-zinc-100 outline-none focus:border-orange-500/50" />
                    <input value={p.qty} onChange={setProductField(i, "qty")} type="number" min="1" placeholder="Qty" className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-center text-zinc-100 outline-none focus:border-orange-500/50" />
                    <select value={p.vat} onChange={setProductField(i, "vat")} className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none focus:border-orange-500/50">
                      <option value={21}>21%</option>
                      <option value={11}>11%</option>
                      <option value={9}>9%</option>
                      <option value={5}>5%</option>
                      <option value={0}>0%</option>
                    </select>
                    <div className="relative">
                      <button
                        data-testid={`mo-row-menu-${i}`}
                        onClick={() => setActionMenuFor(actionMenuFor === i ? null : i)}
                        className="rounded-md p-1.5 text-zinc-300 hover:bg-zinc-800 hover:text-orange-300"
                        title="Acțiuni produs"
                      ><MoreVertical className="h-3.5 w-3.5" /></button>
                      {actionMenuFor === i && (
                        <div className="absolute right-0 z-10 mt-1 w-44 rounded-lg border border-zinc-700 bg-zinc-950 shadow-xl py-1 text-xs">
                          <button onClick={() => { setActionMenuFor(null); document.querySelector(`[data-testid="mo-product-row-${i}"] input`)?.focus(); }}
                            className="block w-full px-3 py-1.5 text-left text-zinc-200 hover:bg-zinc-800">
                            Editează produs
                          </button>
                          <button data-testid={`mo-discount-btn-${i}`} onClick={() => openDiscount(i)}
                            className="block w-full px-3 py-1.5 text-left text-orange-300 hover:bg-orange-500/10">
                            Aplică discount
                          </button>
                          {p.discount && (
                            <button onClick={() => clearDiscount(i)}
                              className="block w-full px-3 py-1.5 text-left text-amber-300 hover:bg-amber-500/10">
                              Șterge discount
                            </button>
                          )}
                          <button onClick={() => { removeProduct(i); setActionMenuFor(null); }}
                            disabled={products.length === 1}
                            className="block w-full px-3 py-1.5 text-left text-rose-300 hover:bg-rose-500/10 disabled:opacity-30">
                            Șterge produs
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Discount summary line (visible when applied) */}
                  {p.discount && p.discount.value > 0 && (
                    <div className="mt-1 ml-1 flex items-center justify-between text-[11px] text-zinc-500">
                      <span className="inline-flex items-center gap-1.5 rounded-md bg-orange-500/10 border border-orange-500/30 px-2 py-0.5 text-orange-300">
                        <Percent className="h-2.5 w-2.5" />
                        Discount {p.discount.type === "percent" ? `${p.discount.value}%` : `${Number(p.discount.value).toFixed(2)} lei`} (-{lineDiscountAmount(p).toFixed(2)} lei)
                      </span>
                      <span className="font-mono text-emerald-300">Total linie: {lineTotal(p).toFixed(2)} lei</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Shipping & totals */}
          <section className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
            <p className="text-[10px] uppercase tracking-wider text-orange-300 mb-2">Livrare & total</p>

            {/* Mode selector: home vs locker */}
            <div className="mb-3 grid grid-cols-2 gap-2">
              <label className={`inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs transition ${
                form.delivery_mode === "home"
                  ? "border-emerald-500 bg-emerald-500/15 text-emerald-200"
                  : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-emerald-500/40"
              }`}>
                <input data-testid="mo-mode-home" type="radio" className="hidden" checked={form.delivery_mode === "home"}
                  onChange={() => setForm({ ...form, delivery_mode: "home" })} />
                📮 Domiciliu ({Number(shipDefaults.home).toFixed(2)} lei)
              </label>
              <label className={`inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs transition ${
                form.delivery_mode === "locker"
                  ? "border-purple-500 bg-purple-500/15 text-purple-200"
                  : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-purple-500/40"
              }`}>
                <input data-testid="mo-mode-locker" type="radio" className="hidden" checked={form.delivery_mode === "locker"}
                  onChange={() => setForm({ ...form, delivery_mode: "locker" })} />
                📦 Locker Easybox ({Number(shipDefaults.locker).toFixed(2)} lei)
              </label>
            </div>

            {/* Payment method selector: ramburs (COD) vs online (card, paid) */}
            <div className="mb-3">
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Metodă plată</label>
              <div className="grid grid-cols-2 gap-2">
                <label className={`inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs transition ${
                  form.payment_mode === "ramburs"
                    ? "border-amber-500 bg-amber-500/15 text-amber-200"
                    : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-amber-500/40"
                }`}>
                  <input data-testid="mo-pay-ramburs" type="radio" className="hidden" checked={form.payment_mode === "ramburs"}
                    onChange={() => setForm({ ...form, payment_mode: "ramburs" })} />
                  💵 Ramburs (COD)
                </label>
                <label className={`inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs transition ${
                  form.payment_mode === "online"
                    ? "border-emerald-500 bg-emerald-500/15 text-emerald-200"
                    : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-emerald-500/40"
                }`}>
                  <input data-testid="mo-pay-online" type="radio" className="hidden" checked={form.payment_mode === "online"}
                    onChange={() => setForm({ ...form, payment_mode: "online" })} />
                  💳 Online — încasată
                </label>
              </div>
            </div>

            {/* Locker selector — visible only for locker mode */}
            {form.delivery_mode === "locker" && (
              <div className="mb-3">
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-purple-300">Locker EasyBox *</label>
                <select
                  data-testid="mo-locker"
                  value={form.locker_id}
                  onChange={(e) => {
                    const lockerId = e.target.value;
                    const l = lockers.find((x) => String(x.lockerId ?? x.id) === String(lockerId));
                    setForm({
                      ...form,
                      locker_id: lockerId,
                      locker_name: l?.name || l?.lockerName || "",
                      locker_address: l?.address || l?.lockerAddress || "",
                    });
                  }}
                  disabled={!form.city || loadingLockers || lockers.length === 0}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-purple-500/50 disabled:opacity-50"
                >
                  <option value="">
                    {!form.city ? "Selectează mai întâi oraș"
                      : loadingLockers ? "Se încarcă lockere..."
                      : lockers.length === 0 ? "Niciun locker disponibil în acest oraș"
                      : `${lockers.length} lockere — alege unul`}
                  </option>
                  {lockers.map((l) => {
                    const id = l.lockerId ?? l.id;
                    const name = l.name || l.lockerName || `Locker ${id}`;
                    const addr = l.address || l.lockerAddress || "";
                    return (
                      <option key={id} value={id}>{name}{addr ? ` — ${addr}` : ""}</option>
                    );
                  })}
                </select>
                {form.locker_id && (
                  <p className="mt-1 text-[10px] text-purple-300/70">
                    Selectat: <strong>{form.locker_name}</strong> · {form.locker_address}
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                  Taxa de livrare ({form.delivery_mode === "locker" ? "BRAG2 locker" : "BRAG1"})
                </label>
                <input data-testid="mo-shipping" value={form.shipping_price} onChange={setField("shipping_price")} type="number" step="0.01" className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
                <p className="text-[10px] text-zinc-500 mt-1">Apare ca linie separată în factură (linkat cu Oblio).</p>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Total comandă (ramburs)</label>
                <p data-testid="mo-grand-total" className="text-lg font-semibold text-emerald-300">{grandTotal.toFixed(2)} RON</p>
                <p className="text-[10px] text-zinc-500">Produse: {productsTotal.toFixed(2)} + transport: {Number(form.shipping_price || 0).toFixed(2)}</p>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-amber-300/80">
              Plată: <strong>{form.payment_mode === "online" ? "online — încasată (card, marcată plătită)" : "ramburs la curier"}</strong> — AWB Sameday {form.delivery_mode === "locker" ? "→ locker EasyBox" : "la domiciliu"}{form.payment_mode === "ramburs" ? ", cu COD = total comandă" : ", fără COD"}.
            </p>
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 px-6 py-3">
          <button onClick={onClose} className="rounded-md border border-zinc-800 bg-zinc-900 px-4 py-1.5 text-xs text-zinc-300 hover:border-zinc-700">Anulează</button>
          <button data-testid="mo-submit" onClick={submit} disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-emerald-600 disabled:opacity-50">
            {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
            Creează comandă
          </button>
        </div>
      </div>

      {/* ── Aplică Discount popover ────────────────────────────────────────── */}
      {discountModalFor !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={() => setDiscountModalFor(null)}>
          <div data-testid="mo-discount-modal" onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl">
            <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
              <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-orange-300">
                <Percent className="h-4 w-4" /> Aplică Discount
              </h3>
              <button onClick={() => setDiscountModalFor(null)} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Tip discount</p>
                <div className="flex gap-2">
                  <label className={`flex-1 inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition ${
                    discountDraft.type === "percent"
                      ? "border-orange-500 bg-orange-500/15 text-orange-200"
                      : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-orange-500/40"
                  }`}>
                    <input data-testid="mo-discount-type-percent" type="radio" className="hidden" checked={discountDraft.type === "percent"}
                      onChange={() => setDiscountDraft({ ...discountDraft, type: "percent" })} />
                    Procent
                  </label>
                  <label className={`flex-1 inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition ${
                    discountDraft.type === "fixed"
                      ? "border-orange-500 bg-orange-500/15 text-orange-200"
                      : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-orange-500/40"
                  }`}>
                    <input data-testid="mo-discount-type-fixed" type="radio" className="hidden" checked={discountDraft.type === "fixed"}
                      onChange={() => setDiscountDraft({ ...discountDraft, type: "fixed" })} />
                    Valoare fixă
                  </label>
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Valoare discount</p>
                <div className="relative">
                  <input
                    data-testid="mo-discount-value"
                    type="number"
                    step="0.01"
                    min="0"
                    value={discountDraft.value}
                    onChange={(e) => setDiscountDraft({ ...discountDraft, value: e.target.value })}
                    autoFocus
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 pr-12 text-sm text-zinc-100 outline-none focus:border-orange-500/50"
                  />
                  <span className="absolute inset-y-0 right-3 flex items-center text-sm text-zinc-500">
                    {discountDraft.type === "percent" ? "%" : "lei"}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-zinc-800 px-5 py-3">
              <button onClick={() => setDiscountModalFor(null)} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-700">Anulează</button>
              <button data-testid="mo-apply-discount" onClick={applyDiscount}
                className="rounded-md bg-orange-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-orange-600">
                Aplică Discount
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Autocomplete cell (shared SKU + name dropdown) ----------
function MoAutocomplete({ testid, value, placeholder, onChange, onPick, suggestions, mono }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  useEffect(() => { setActive(0); }, [suggestions.length, value]);
  const close = () => setTimeout(() => setOpen(false), 120);
  const onKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const p = suggestions[active];
      if (p) { onPick(p); setOpen(false); }
    } else if (e.key === "Escape") { setOpen(false); }
  };
  return (
    <div className="relative">
      <input
        data-testid={testid}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={close}
        onKeyDown={onKey}
        className={`w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs ${mono ? "font-mono" : ""} text-zinc-100 outline-none focus:border-orange-500/50`}
      />
      {open && suggestions.length > 0 && (
        <div data-testid={`${testid}-dropdown`}
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950 shadow-2xl">
          {suggestions.map((p, i) => (
            <button type="button" key={p.sku}
              data-testid={`${testid}-opt-${i}`}
              onMouseDown={(e) => { e.preventDefault(); onPick(p); setOpen(false); }}
              onMouseEnter={() => setActive(i)}
              className={`block w-full px-2 py-1.5 text-left text-[11px] ${i === active ? "bg-orange-500/15 text-orange-100" : "text-zinc-200 hover:bg-zinc-900"}`}>
              <span className="font-mono font-semibold text-orange-300">{p.sku}</span>
              <span className="ml-2 text-zinc-400">{(p.name || "").slice(0, 80)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
